import React from "react";
import { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import {
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormGroup,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
// import ReactGA from "react-ga4";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";

import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import { useNavigate, useLocation } from "react-router-dom";
import HappyFace from "../../HappyFace.png";
import SadFace from "../../SadFace.png";
import useGAEventsTracker from "../../GaEventTracker";
import CloseIcon from "@mui/icons-material/Close";
import { emailDispatch } from "../utils";

const questionDetails = {
  graves: {
    question: "Do you have a family history of graves disease?",
    options: [
      { title: "Yes", value: true },
      { title: "No", value: false },
    ],
  },
  hashimoto: {
    question: "Do you have a family history of hashimoto disease?",
    options: [
      { title: "Yes", value: true },
      { title: "No", value: false },
    ],
  },
  thyroid: {
    question: "Do you have a family history of thyroid disease?",
    options: [
      { title: "Yes", value: true },
      { title: "No", value: false },
    ],
  },
  autoimmune: {
    question: "Do you have a history of autoimmune disease?",
    options: [
      { title: "Yes", value: true },
      { title: "No", value: false },
    ],
  },
  t3: {
    question: "Status of Free T3",
    options: [
      { title: "Low", value: "low" },
      { title: "Normal", value: "normal" },
      { title: "High", value: "high" },
    ],
  },
  t4: {
    question: "Status of Free T4",
    options: [
      { title: "Low", value: "low" },
      { title: "Normal", value: "normal" },
      { title: "High", value: "high" },
    ],
  },
  symptomDetails: {
    question: "Select Any Symptoms You Currently Have",
    options: [
      { title: "Weight Gain", value: "Weight gain", isChecked: false },
      { title: "Fatigue", value: "fatigue", isChecked: false },
      { title: "Dry Skin", value: "dry skin", isChecked: false },
      { title: "Hair Loss", value: "hair loss", isChecked: false },
      { title: "Constipation", value: "Constipation", isChecked: false },
      {
        title: "Irregular Periods",
        value: "irregular periods",
        isChecked: false,
      },
    ],
  },
};

const ThyroidRiskForm = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const {
    riskType,
    clientId,
    labNo,
    patient_age,
    patient_gender,
    tsh,
    tsh_unit,
    anti_tpo,
    anti_tpo_unit,
    t3,
    t3_unit,
    t3_indicator,
    t4,
    t4_unit,
    t4_indicator,
    familyHistoryOfThyroid,
    preExistingConditions,
  } = Object.fromEntries(queryParams);

  const preExistingConditionsDetails = JSON.parse(preExistingConditions);

  const [age, setAge] = useState(patient_age ? patient_age : "");
  const [gender, setGender] = useState(
    patient_gender ? patient_gender : "male"
  );
  const [tshValue, setTshValue] = useState(tsh || "");
  const [antiTpoValue, setAntiTpoValue] = useState(anti_tpo || "");
  const [tshUnit, setTshUnit] = useState(tsh_unit || "mIU/L");
  const [antiTpoUnit, setAntiTpoUnit] = useState(anti_tpo_unit || "kU/L");
  const [t3Details, setT3Details] = useState({
    value: t3 || "",
    unit: t3_unit || "",
    indicator: t3_indicator || "normal",
  });
  const [t4Details, setT4Details] = useState({
    value: t4 || "",
    unit: t4_unit || "",
    indicator: t4_indicator || "normal",
  });

  const [thyroidHistoryStatus, setThyroidHistoryStatus] = useState(
    familyHistoryOfThyroid ? JSON.parse(familyHistoryOfThyroid) : false
  );

  const [autoimmuneDiseaseStatus, setAutoimmuneDiseaseStatus] = useState(
    preExistingConditionsDetails?.autoimmuneDiseaseHistory
      ? preExistingConditionsDetails?.autoimmuneDiseaseHistory
      : false
  );

  const [symptoms, setSymptoms] = useState(
    questionDetails.symptomDetails.options
  );

  const [checked, setChecked] = useState(false);
  const [checkBoxError, setCheckBoxError] = useState(false);
  const [open, setOpen] = useState(false);

  const [riskScore, setRiskScore] = useState("");
  const [loader, setLoader] = useState(false);

  const navigate = useNavigate();
  const GAEventsTracker = useGAEventsTracker("risk score form page");

  function getNumericValue(value) {
    let result = "";
    if (value && !isNaN(parseFloat(value))) {
      result = parseFloat(value);
    }

    return result;
  }

  function getFormattedSymptoms(symptomsData) {
    let result = [];
    symptomsData.forEach((each) => {
      if (each.isChecked) {
        result.push(each.value);
      }
    });
    return result;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!checked) {
      setCheckBoxError("Please check this box");
      return;
    }

    if (checked) {
      setCheckBoxError("");
    }
    setLoader(true);
    const url = `https://6ohh9xcizb.execute-api.ap-south-1.amazonaws.com/prod/?riskType=${riskType}`;

    const payload = {
      clientId: clientId,
      labNo: labNo,
      age: getNumericValue(age),
      gender: gender,
      results: [
        {
          name: "TSH",
          value: getNumericValue(tshValue),
          unit: tshUnit,
        },
        {
          name: "Anti-TPO",
          value: getNumericValue(antiTpoValue),
          unit: antiTpoUnit,
        },
        {
          name: "T3",
          value: getNumericValue(t3Details.value),
          unit: t3Details.unit,
          indicator: t3Details.indicator,
        },
        {
          name: "T4",
          value: getNumericValue(t4Details.value),
          unit: t4Details.unit,
          indicator: t4Details.indicator,
        },
      ],
      familyHistory: {
        has_thyroid: thyroidHistoryStatus,
      },
      preExistingConditions: {
        thyroidSymptoms: getFormattedSymptoms(symptoms),
        autoimmuneDiseaseHistory: autoimmuneDiseaseStatus,
      },
      isPartialRiskScore: false,
    };

    const userFilledData = {
      labNo,
      has_thyroid: thyroidHistoryStatus,
      thyroidSymptoms: getFormattedSymptoms(symptoms),
      autoimmuneDiseaseHistory: autoimmuneDiseaseStatus,
    };

    const options = {
      headers: {
        "Content-Type": "application/json",
      },
      method: "POST",
      body: JSON.stringify(payload),
    };

    try {
      const response = await fetch(url, options);
      const data = await response.json();

      if (data.isSuccess && data?.data?.risk) {
        GAEventsTracker("submitted", "successfully", "diabetes risk");
        GAEventsTracker("risk score data", data.data.risk, "diabetes risk");
        setRiskScore(data.data.risk);
        await emailDispatch(userFilledData, riskType, clientId);
        Swal.fire({
          iconHtml: `<img src=${HappyFace}>`,
          customClass: {
            icon: "no-border",
          },
          width: "400px",
          title: "Success!",
          text: "Your risk score has been generated successfully.",
        });
        navigate("/riskScore", {
          state: {
            riskData: data.data.risk,
            riskType: riskType,
            clientId: clientId,
          },
        });
      } else {
        GAEventsTracker("submission", "unsuccessful");
        Swal.fire({
          iconHtml: `<img src=${SadFace}>`,
          customClass: {
            icon: "no-border",
          },
          width: "400px",
          title: "Error",
          text: "Something went wrong. Pleasse try again.",
        });
      }
    } catch (error) {
      console.log("Risk score error", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error,
      });
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    if (checked) {
      setCheckBoxError("");
    }
  }, [checked]);

  useEffect(() => {
    if (preExistingConditionsDetails?.thyroidSymptoms?.length) {
      const updatedExistingSymptoms = symptoms.map((each) =>
        preExistingConditionsDetails.thyroidSymptoms.includes(each.value)
          ? { ...each, isChecked: true }
          : each
      );
      setSymptoms(updatedExistingSymptoms);
    }
  }, []);

  const handleCheckboxSelection = (e) => {
    const updated = symptoms.map((each) =>
      each.title === e.target.name
        ? { ...each, isChecked: e.target.checked }
        : each
    );
    setSymptoms(updated);
  };

  return (
    <Stack
      sx={{ padding: { md: "30px", sm: "20px", xs: "0px" } }}
      direction="row"
      justifyContent={"center"}
    >
      <Box
        p={4}
        sx={{
          width: { md: "80%", sm: "100%", xs: "100%" },
          background: "#EDF9FE",
          borderRadius: "16px",
        }}
      >
        <Typography variant="h5">Basic Information</Typography>
        <form onSubmit={handleSubmit}>
          <Stack direction={"column"} gap={2.5} mt={3}>
            <Stack
              gap={3}
              sx={{
                flexDirection: { md: "row", sm: "column", xs: "column" },
                alignItems: {
                  md: "center",
                  sm: "flex-start",
                  xs: "flex-start",
                },
              }}
            >
              <Box>
                <TextField
                  fullWidth
                  size={"small"}
                  label="Age"
                  placeholder="age should be between 20-75"
                  variant="outlined"
                  onChange={(e) => setAge(e.target.value)}
                  value={age}
                  required
                  maxWidth="sm"
                />
              </Box>
              <Box>
                <FormControl
                  onChange={(e) => setGender(e.target.value)}
                  value={gender}
                  sx={{
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <FormLabel sx={{ marginRight: "16px", width: "70px" }}>
                    Gender
                  </FormLabel>
                  <RadioGroup row defaultValue={gender}>
                    <FormControlLabel
                      value="male"
                      control={<Radio sx={{ padding: "6px" }} size="small" />}
                      label="Male"
                    />
                    <FormControlLabel
                      value="female"
                      control={<Radio sx={{ padding: "6px" }} size="small" />}
                      label="Female"
                    />
                  </RadioGroup>
                </FormControl>
              </Box>
            </Stack>
            <Stack
              gap={3}
              sx={{
                flexDirection: { md: "row", sm: "column", xs: "column" },
                alignItems: {
                  md: "center",
                  sm: "flex-start",
                  xs: "flex-start",
                },
              }}
            >
              <Box flexGrow={1}>
                <Typography>TSH</Typography>
                <Stack direction={"row"} alignItems={"center"} gap={0.5} mt={1}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Value"
                    placeholder="Enter Value"
                    variant="outlined"
                    onChange={(e) => setTshValue(e.target.value)}
                    value={tshValue}
                    required
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label="Unit"
                    placeholder="Enter Unit"
                    variant="outlined"
                    onChange={(e) => setTshUnit(e.target.value)}
                    value={tshUnit}
                    required
                  />
                </Stack>
              </Box>
              <Box flexGrow={1}>
                <Typography>TPO Antibodies</Typography>
                <Stack direction={"row"} alignItems={"center"} gap={0.5} mt={1}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Value"
                    placeholder="Enter Value"
                    variant="outlined"
                    onChange={(e) => setAntiTpoValue(e.target.value)}
                    value={antiTpoValue}
                    required
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label="Unit"
                    placeholder="Enter Unit"
                    variant="outlined"
                    onChange={(e) => setAntiTpoUnit(e.target.value)}
                    value={antiTpoUnit}
                    required
                  />
                </Stack>
              </Box>
            </Stack>

            <Box>
              <FormControl required>
                <FormLabel
                  id="demo-radio-buttons-group-label"
                  sx={{ color: "#3567D6" }}
                >
                  {questionDetails.t3.question}
                </FormLabel>
                <RadioGroup
                  aria-labelledby="demo-radio-buttons-group-label"
                  value={t3Details.indicator}
                  name="radio-buttons-group"
                  onChange={(e) =>
                    setT3Details((prev) => ({
                      ...prev,
                      indicator: e.target.value,
                    }))
                  }
                  row
                >
                  {questionDetails.t3.options.map((each) => (
                    <FormControlLabel
                      key={each.title}
                      value={each.value}
                      control={<Radio size="small" />}
                      label={each.title}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            </Box>

            <Box>
              <FormControl required>
                <FormLabel
                  id="demo-radio-buttons-group-label"
                  sx={{ color: "#3567D6" }}
                >
                  {questionDetails.t4.question}
                </FormLabel>
                <RadioGroup
                  aria-labelledby="demo-radio-buttons-group-label"
                  value={t4Details.indicator}
                  name="radio-buttons-group"
                  onChange={(e) =>
                    setT4Details((prev) => ({
                      ...prev,
                      indicator: e.target.value,
                    }))
                  }
                  row
                >
                  {questionDetails.t4.options.map((each) => (
                    <FormControlLabel
                      key={each.title}
                      value={each.value}
                      control={<Radio size="small" />}
                      label={each.title}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            </Box>

            {/* symptoms */}

            <Box>
              <Typography sx={{ color: "#3567D6" }}>
                {questionDetails.symptomDetails.question}
              </Typography>
              <FormGroup sx={{ flexDirection: "row" }}>
                {symptoms.map((each) => (
                  <FormControlLabel
                    key={each.title}
                    control={
                      <Checkbox
                        size="small"
                        checked={each.isChecked}
                        onChange={handleCheckboxSelection}
                        name={each.title}
                      />
                    }
                    label={each.title}
                  />
                ))}
              </FormGroup>
            </Box>

            <Box>
              <FormControl required>
                <FormLabel
                  id="demo-radio-buttons-group-label"
                  sx={{ color: "#3567D6" }}
                >
                  {questionDetails.autoimmune.question}
                </FormLabel>
                <RadioGroup
                  aria-labelledby="demo-radio-buttons-group-label"
                  value={autoimmuneDiseaseStatus}
                  name="radio-buttons-group"
                  onChange={(e) =>
                    setAutoimmuneDiseaseStatus(e.target.value === "true")
                  }
                  row
                >
                  {questionDetails.autoimmune.options.map((each) => (
                    <FormControlLabel
                      key={each.title}
                      value={each.value}
                      control={<Radio size="small" />}
                      label={each.title}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            </Box>

            <Box>
              <FormControl required>
                <FormLabel
                  id="demo-radio-buttons-group-label"
                  sx={{ color: "#3567D6" }}
                >
                  {questionDetails.thyroid.question}
                </FormLabel>
                <RadioGroup
                  aria-labelledby="demo-radio-buttons-group-label"
                  value={thyroidHistoryStatus}
                  name="radio-buttons-group"
                  onChange={(e) =>
                    setThyroidHistoryStatus(e.target.value === "true")
                  }
                  row
                >
                  {questionDetails.thyroid.options.map((each) => (
                    <FormControlLabel
                      key={each.title}
                      value={each.value}
                      control={<Radio size="small" />}
                      label={each.title}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            </Box>

            <Box>
              <Stack direction="row" alignItems="center">
                <Checkbox
                  onChange={() => setChecked(!checked)}
                  checked={checked}
                />
                <Typography variant="p">
                  I acknowledge & agree that I’m sharing metabolic data for
                  accurate results.
                </Typography>
              </Stack>
              {checkBoxError && (
                <Typography
                  pl={5}
                  variant="p"
                  sx={{ color: "red", fontSize: "12px" }}
                >
                  {checkBoxError}
                </Typography>
              )}
            </Box>
            <Box>
              <Button
                type="submit"
                variant="contained"
                color="success"
                sx={{ width: { md: "40%", sm: "100%", xs: "100%" } }}
              >
                {loader ? (
                  <CircularProgress sx={{ color: "#fff" }} size="30px" />
                ) : (
                  "Get Thyroid Risk Score"
                )}
              </Button>
            </Box>
          </Stack>
        </form>
      </Box>
    </Stack>
  );
};

export default ThyroidRiskForm;
